import React from 'react';
import { ReceiptData } from '../../types/dailyReport.ts';
import { ValidationError } from '../../features/daily-report/types.ts';
import Alert from '../common/Alert.tsx';
import DataTable from '../common/DataTable.tsx';

interface ReceiptInputProps {
  receiptData: ReceiptData;
  validationErrors: ValidationError[];
  currentDate: string;
}

// 디버그 로그 설정
const DEBUG_MODE = {
  TEST_LOGS: false,
  ALL: false
};

const debugLog = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
  if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
    console.log(...args);
  }
};

const ReceiptInput: React.FC<ReceiptInputProps> = ({
  receiptData,
  validationErrors,
  currentDate
}) => {
  // 현재 선택된 날짜에 맞는 항목 필터링
  const filteredItems = receiptData.items.filter(item => 
    item.receiptDate === currentDate
  );
  
  const rowClassName = (row: any) => {
    return row.hasError ? "bg-red-100" : "";
  };
  
  const footerData = {
    '차트번호': '합계',
    '이름': '',
    '어시스트': '',
    '실제수납액': filteredItems.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString(),
    '할인액': filteredItems.reduce((sum, item) => sum + (item.discount || 0), 0).toLocaleString(),
    '현금수납': filteredItems.reduce((sum, item) => sum + (item.cashAmount || 0), 0).toLocaleString(),
    '계좌이체': filteredItems.reduce((sum, item) => sum + (item.transferAmount || 0), 0).toLocaleString(),
    '카드수납': filteredItems.reduce((sum, item) => sum + (item.cardAmount || 0), 0).toLocaleString(),
    '미수(+)선수(-)': filteredItems.reduce((sum, item) => sum + (item.uncollectedAmount || 0), 0).toLocaleString(),
    '청구액': filteredItems.reduce((sum, item) => sum + (item.insuranceClaim || 0), 0).toLocaleString(),
    '건강생활유지비': filteredItems.reduce((sum, item) => sum + (item.healthLifeSupport || 0), 0).toLocaleString(),
    '총수납': filteredItems.reduce((sum, item) => sum +
                (item.cashAmount || 0) +
                (item.transferAmount || 0) +
                (item.cardAmount || 0) +
                (item.insuranceClaim || 0) +
                (item.healthLifeSupport || 0), 0).toLocaleString(),
    '메모': ''
  };
  
  // 현재 날짜에 해당하는 오류만 필터링
  const filteredErrors = validationErrors.filter(() => 
    true // 추후 error.receiptDate === currentDate 로 변경 가능
  );
  
  return (
    <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">수납 입력</h2>
        <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
          📅 {currentDate}
        </div>
      </div>
      
      {filteredErrors.length > 0 && (
        <Alert type="error" title="총진료비와 결제 금액 합계가 일치하지 않는 항목이 있습니다:">
          <ul className="list-disc pl-5">
            {filteredErrors.map((error, idx) => (
              <li key={idx}>
                [{error.receiptDate}] {error.patientName} (차트번호: {error.chartNumber || '없음'}) - 
                총진료비: {error.totalFee.toLocaleString()}원, 
                결제합계: {error.paymentSum.toLocaleString()}원, 
                차이: {error.diff.toLocaleString()}원
              </li>
            ))}
          </ul>
        </Alert>
      )}
      
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
            <DataTable
              data={filteredItems.map(item => ({
                ...item,
                chartNumber: item.chartNumber,
                name: item.name,
                assistName: item.assistName,
                '실제수납액': item.amount || 0,
                '할인액': item.discount || 0,
                '현금수납': item.cashAmount,
                '계좌이체': item.transferAmount,
                '카드수납': item.cardAmount,
                '미수(+)선수(-)': item.uncollectedAmount,
                '청구액': item.insuranceClaim,
                '건강생활유지비': item.healthLifeSupport || 0,
                '총수납': (item.cashAmount || 0) +
                  (item.transferAmount || 0) +
                  (item.cardAmount || 0) +
                  (item.insuranceClaim || 0) +
                  (item.healthLifeSupport || 0),
                '메모': item.hasError ? "금액 불일치" : ""
              }))}
              columns={[
                { key: 'chartNumber', header: '차트번호', sortable: true, width: 'w-20 sm:w-24' },
                { key: 'name', header: '이름', sortable: true, width: 'w-16 sm:w-20' },
                { key: 'assistName', header: '어시스트', sortable: true, width: 'w-20 sm:w-24' },
                { key: '실제수납액', header: '실제수납액', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-24 sm:w-28' },
                { key: '할인액', header: '할인액', align: 'right', sortable: true, render: (value) => {
                  if (typeof value !== 'number') return value;
                  const colorClass = value > 0 ? 'text-red-600' : '';
                  return <span className={colorClass}>{value.toLocaleString()}</span>;
                }, width: 'w-20 sm:w-24' },
                { key: '현금수납', header: '현금수납', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-20 sm:w-24' },
                { key: '계좌이체', header: '계좌이체', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-20 sm:w-24' },
                { key: '카드수납', header: '카드수납', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-20 sm:w-24' },
                { key: '미수(+)선수(-)', header: '미수(+)선수(-)', align: 'right', sortable: true, render: (value) => {
                  if (typeof value !== 'number') return value;
                  const colorClass = value > 0 ? 'text-red-600' : value < 0 ? 'text-green-600' : '';
                  return <span className={colorClass}>{value.toLocaleString()}</span>;
                }, width: 'w-24 sm:w-28' },
                { key: '청구액', header: '청구액', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-20 sm:w-24' },
                { key: '건강생활유지비', header: '건강생활유지비', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-28 sm:w-32' },
                { key: '총수납', header: '총수납', align: 'right', sortable: true, render: (value) => typeof value === 'number' ? value.toLocaleString() : value, width: 'w-20 sm:w-24' },
                { key: '메모', header: '메모', sortable: false, width: 'w-20 sm:w-24' }
              ]}
              keyField="chartNumber"
              showFooter={true}
              footerData={footerData}
              rowClassName={rowClassName}
              sortable={true}
            />
          </div>
        </div>
      </div>
      
      {filteredItems.length === 0 && (
        <div className="text-center py-12 sm:py-16">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">수납 데이터가 없습니다</h3>
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
            선택한 날짜 <span className="font-medium text-gray-700">{currentDate}</span>에 해당하는 수납 데이터가 없습니다.
            <br className="hidden sm:block" />
            Excel 파일을 업로드하여 데이터를 추가해보세요.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReceiptInput;